import { useCallback, useEffect, useState } from "react";
import { isMailEmbeddedInHub } from "@/auth/session";

export function isMailPwaStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return true;
  }
  const installedModes = [
    "standalone",
    "fullscreen",
    "minimal-ui",
    "window-controls-overlay",
  ] as const;
  return installedModes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches);
}

export function isMailIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

type MailPwaBridge = {
  isStandalone: boolean;
  isIos: boolean;
  canNativeInstall: boolean;
  showInstallUi: boolean;
  promptInstall: () => Promise<boolean>;
};

function readMailPwaBridge(): MailPwaBridge {
  const standalone = isMailPwaStandalone();
  const embedded = isMailEmbeddedInHub();
  const bridge = (window as Window & { mailPwa?: MailPwaBridge }).mailPwa;
  return {
    isStandalone: standalone,
    isIos: bridge?.isIos ?? isMailIos(),
    canNativeInstall: bridge?.canNativeInstall ?? false,
    showInstallUi: !standalone && !embedded,
    promptInstall: bridge?.promptInstall ?? (async () => false),
  };
}

/** UI install — état synchronisé avec public/pwa.js (SpeedEats). */
export function useMailPwaInstall() {
  const [state, setState] = useState(readMailPwaBridge);

  useEffect(() => {
    const sync = () => setState(readMailPwaBridge());
    sync();
    window.addEventListener("mail-pwa-sync", sync);
    window.addEventListener("beforeinstallprompt", sync);
    window.addEventListener("appinstalled", sync);
    const onDisplayMode = () => sync();
    window.matchMedia("(display-mode: standalone)").addEventListener("change", onDisplayMode);
    window.matchMedia("(display-mode: window-controls-overlay)").addEventListener("change", onDisplayMode);
    return () => {
      window.removeEventListener("mail-pwa-sync", sync);
      window.removeEventListener("beforeinstallprompt", sync);
      window.removeEventListener("appinstalled", sync);
      window.matchMedia("(display-mode: standalone)").removeEventListener("change", onDisplayMode);
      window.matchMedia("(display-mode: window-controls-overlay)").removeEventListener("change", onDisplayMode);
    };
  }, []);

  const install = useCallback(async () => {
    const bridge = readMailPwaBridge();
    return bridge.promptInstall();
  }, []);

  return {
    isStandalone: state.isStandalone,
    canNativeInstall: state.canNativeInstall,
    isIos: state.isIos,
    showInstallUi: state.showInstallUi,
    install,
  };
}

declare global {
  interface Window {
    mailPwa?: MailPwaBridge;
  }
}
